export function formatCellNumber(cell) {
  if (!cell) return '';
  let digits = cell.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+1-${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (cell.startsWith('+1') && digits.length === 11) {
    return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return cell;
}

/**
 * Format a monetary amount in cents to a currency string
 * @param {number} cents - The amount in cents
 * @param {string} [currency='USD'] - The currency code (default: USD)
 * @returns {string} Formatted currency string
 */
// Map of currency codes to their symbols
const CURRENCY_SYMBOLS = {
  'EUR': '€',
  'GBP': '£',
  'JPY': '¥',
  'INR': '₹',
  'AUD': 'A$',
  'CAD': 'C$',
  'CNY': '¥',
  'NZD': 'NZ$',
  'SGD': 'S$',
  'ZAR': 'R'
};

export function formatCurrencyCents(cents, currency = 'USD') {
  const amount = (typeof cents === 'number' ? (cents / 100).toFixed(2) : '0.00');
  const currencyCode = currency.toUpperCase();

  // Special case for USD - show as 'USD$ 150.00'
  if (currencyCode === 'USD') {
    return `USD$ ${amount}`;
  }

  // Special handling for INR which typically shows symbol before amount without space
  if (currencyCode === 'INR') {
    return `₹${amount}`;
  }

  // For other currencies, use their respective symbols if available
  const symbol = CURRENCY_SYMBOLS[currencyCode];
  if (symbol) {
    return `${symbol}${amount}`;
  }

  // Fallback for unknown currencies
  return `${currencyCode} ${amount}`;
}

/**
 * Format a number with thousand separators
 * @param {number|string} value - The number to format
 * @returns {string} Formatted number string or '-' if invalid
 */
export function formatNumber(value) {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return num.toLocaleString();
}

/**
 * Get the current currency symbol from localStorage
 * @returns {string} Currency symbol
 */
function getCurrencySymbol() {
  const currency = localStorage.getItem('reopt_selectedCurrency') || 'USD';
  const currencyCode = currency.toUpperCase();

  // Return symbol based on currency code
  if (currencyCode === 'USD') return '$';
  return CURRENCY_SYMBOLS[currencyCode] || '$';
}

/**
 * Format a value that is ALREADY IN WHOLE CURRENCY UNITS - dollars, not cents.
 *
 * Named for its unit deliberately. This used to be called formatCurrency, which
 * collided with the global formatCurrency in vue-mixins.js - that one takes cents
 * and divides by 100, this one does not. Importing this shadowed the mixin inside
 * the module scope, so two identical-looking call sites in two files rendered
 * values 100x apart with nothing on the line to say which was which. That collision
 * was the source of six money-display bugs.
 *
 * Money is stored in cents throughout this codebase, so a raw model attribute
 * belongs in formatCurrencyCents or the mixin. Reach for this only when the caller
 * has already converted.
 *
 * @param {number|string} value - The value to format, in whole units
 * @param {number} precision - Number of decimal places (default: 0)
 * @returns {string} Formatted currency string
 */
export function formatCurrencyUnits(value, precision = 0) {
  const symbol = getCurrencySymbol();
  if (!value && value !== 0) return `${symbol}0.00`;
  const num = Number(value);
  return `${symbol}${num.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  })}`;
}

/**
 * Format large numbers with K, M, B suffixes (shorthand currency)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted shorthand currency string
 */
export function formatShorthandCurrency(value) {
  const symbol = getCurrencySymbol();

  // Handle null, undefined, empty string
  if (value === null || value === undefined || value === '') return `${symbol}0`;

  // If it's already formatted with a suffix at the end (like "$10.7M"), return it
  if (typeof value === 'string' && /[KMB]$/.test(value)) {
    return value;
  }

  // Convert to number, removing any commas or other formatting
  const num = typeof value === 'string' ? Number(value.replace(/[^0-9.-]/g, '')) : Number(value);

  // Handle NaN or invalid numbers
  if (isNaN(num)) return `${symbol}0`;

  if (num === 0) return `${symbol}0`;

  // Format large numbers with K, M, B suffixes
  if (num >= 1000000000) {
    return `${symbol}${(num / 1000000000).toFixed(1)}B`;
  }
  if (num >= 1000000) {
    return `${symbol}${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${symbol}${(num / 1000).toFixed(1)}K`;
  }

  return `${symbol}${num.toLocaleString('en-US')}`;
}

/**
 * Format a date string to MM/DD/YYYY format
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date string (MM/DD/YYYY)
 */
export function formatDate(date) {
  if (!date) return '-';
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '-';

  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const year = dateObj.getFullYear();

  return `${month}/${day}/${year}`;
}

/**
 * Format a percentage value
 * @param {number} value - The percentage value (0-100)
 * @param {number} precision - Number of decimal places (default: 0)
 * @returns {string} Formatted percentage string
 */
export function formatPercentage(value, precision = 0) {
  if (!value && value !== 0) return '0%';
  return `${Number(value).toFixed(precision)}%`;
}

/**
 * Format large numbers with K, M, B suffixes (without currency symbol)
 * 
 * Usage in Vue components:
 *   this.formatCompactNumber(1000)        // "1.0K"
 *   this.formatCompactNumber(1500)        // "1.5K"
 *   this.formatCompactNumber(1000000)     // "1.0M"
 *   this.formatCompactNumber(2500000)     // "2.5M"
 *   this.formatCompactNumber(1000000000)  // "1.0B"
 *   this.formatCompactNumber(1234, 0)     // "1K" (no decimals)
 *   this.formatCompactNumber(1234, 2)     // "1.23K" (2 decimals)
 * 
 * @param {number|string} value - The value to format
 * @param {number} precision - Number of decimal places (default: 1)
 * @returns {string} Formatted shorthand number string (e.g., "1.5K", "2.3M", "1.2B")
 */
export function formatCompactNumber(value, precision = 1) {
  // Handle null, undefined, empty string
  if (value === null || value === undefined || value === '') return '0';

  // If it's already formatted with a suffix at the end (like "10.7M"), return it
  if (typeof value === 'string' && /[KMB]$/.test(value)) {
    return value;
  }

  // Convert to number, removing any commas or other formatting
  const num = typeof value === 'string' ? Number(value.replace(/[^0-9.-]/g, '')) : Number(value);

  // Handle NaN or invalid numbers
  if (isNaN(num)) return '0';

  if (num === 0) return '0';

  // Handle negative numbers
  const isNegative = num < 0;
  const absNum = Math.abs(num);

  // Format large numbers with K, M, B suffixes
  let formatted;
  if (absNum >= 1000000000) {
    formatted = `${(absNum / 1000000000).toFixed(precision)}B`;
  } else if (absNum >= 1000000) {
    formatted = `${(absNum / 1000000).toFixed(precision)}M`;
  } else if (absNum >= 1000) {
    formatted = `${(absNum / 1000).toFixed(precision)}K`;
  } else {
    // For numbers less than 1000, show with commas
    formatted = absNum.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: precision
    });
  }

  return isNegative ? `-${formatted}` : formatted;
}
